import { memo, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import mermaid from 'mermaid';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.css';

// Mermaid 초기화
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'inherit',
});

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Mermaid 다이어그램 렌더링 컴포넌트
const MermaidDiagram = memo(function MermaidDiagram({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code.trim()) return;

      try {
        const id = `mermaid-${Math.random().toString(36).substring(2, 9)}`;
        const { svg } = await mermaid.render(id, code.trim());
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError('다이어그램을 렌더링할 수 없습니다');
      }
    };

    renderDiagram();
  }, [code]);

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
        <p>{error}</p>
        <pre className="mt-2 overflow-x-auto text-xs">{code}</pre>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="my-4 flex justify-center overflow-x-auto overflow-y-auto max-h-[500px] rounded-lg border bg-white p-4 dark:bg-slate-900"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
});

// 코드 블록 컴포넌트 (복사 버튼 포함)
const CodeBlock = memo(function CodeBlock({
  language,
  code,
}: {
  language: string | null;
  code: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Mermaid 다이어그램 처리
  if (language === 'mermaid') {
    return <MermaidDiagram code={code} />;
  }

  return (
    <div className="group relative my-4 max-w-full overflow-hidden">
      {language && (
        <div className="flex items-center justify-between rounded-t-lg border border-b-0 border-slate-700 bg-slate-800 px-4 py-2">
          <span className="text-xs font-medium text-slate-400">{language}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-slate-400 hover:text-white"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <Check className="mr-1 h-3 w-3" />
                복사됨
              </>
            ) : (
              <>
                <Copy className="mr-1 h-3 w-3" />
                복사
              </>
            )}
          </Button>
        </div>
      )}
      <pre
        className={cn(
          'overflow-x-auto bg-slate-900 p-4 text-sm',
          language ? 'rounded-b-lg' : 'rounded-lg'
        )}
      >
        <code className={cn('font-mono', language && `language-${language}`)}>
          {code}
        </code>
      </pre>
      {!language && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-2 top-2 h-6 px-2 text-xs text-slate-400 opacity-0 transition-opacity hover:text-white group-hover:opacity-100"
          onClick={handleCopy}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </Button>
      )}
    </div>
  );
});

export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  className,
}: MarkdownRendererProps) {
  return (
    <div className={cn('prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeRaw, rehypeHighlight]}
        components={{
          // 코드 블록 처리
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : null;
            const codeString = String(children).replace(/\n$/, '');

            // 인라인 코드인지 확인 (부모가 pre가 아님)
            const isInline = !className && codeString.indexOf('\n') === -1;

            if (isInline) {
              return (
                <code
                  className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm"
                  {...props}
                >
                  {children}
                </code>
              );
            }

            return <CodeBlock language={language} code={codeString} />;
          },
          // 테이블 스타일링
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto rounded-lg border">
              <table className="w-full">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted/50">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="border-b px-4 py-2 text-left font-semibold">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border-b px-4 py-2">{children}</td>
          ),
          // 링크 스타일링
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            >
              {children}
            </a>
          ),
          // 블록쿼트 스타일링
          blockquote: ({ children }) => (
            <blockquote className="my-4 border-l-4 border-primary/30 bg-muted/30 py-2 pl-4 italic">
              {children}
            </blockquote>
          ),
          // 이미지 스타일링
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="my-4 max-w-full rounded-lg shadow-md"
            />
          ),
          // 수평선
          hr: () => <hr className="my-6 border-muted" />,
          // 리스트 스타일링
          ul: ({ children }) => (
            <ul className="my-2 list-disc pl-6 marker:text-muted-foreground">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 list-decimal pl-6 marker:text-muted-foreground">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="my-1">{children}</li>
          ),
          // 체크박스 리스트 (GFM task list)
          input: ({ type, checked, disabled }) => {
            if (type === 'checkbox') {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  className="mr-2 h-4 w-4 rounded border-muted-foreground"
                  readOnly
                />
              );
            }
            return null;
          },
          // 헤딩 스타일링
          h1: ({ children }) => (
            <h1 className="mb-4 mt-6 text-2xl font-bold">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-3 mt-5 text-xl font-semibold">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-2 mt-4 text-lg font-semibold">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="mb-2 mt-3 text-base font-semibold">{children}</h4>
          ),
          // 단락
          p: ({ children }) => (
            <p className="my-2 leading-relaxed">{children}</p>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
